import type { KpiItem, PricePackBubbleRow } from 'hooks/ppa/useFetchPricePackArchitecture';

import { normalizePackArchitectureKpiId } from 'components/pages/PackArchitecture/packArchitectureKpiIdMatching.helpers';

export type KpiOption = {
    id: string;
    label: string;
};

export type TableColumn = {
    key: string;
    label: string;
};

export type CellKpiEntry = {
    formatted: string;
    growthPct: number | null;
};

export type CellData = Record<string, CellKpiEntry>;

export type TableRow = {
    key: string;
    label: string;
    cells: Record<string, CellData>;
};

export const MAX_VISIBLE_COLUMNS = 100;
export const MAX_KPI_SELECTION = 3;
export const TABLE_MIN_METRIC_SELECTION_MESSAGE = 'At least 1 metric must be selected.';
/** Match chart horizontal pagination — only fetch when scrolled to the right edge. */
export const HORIZONTAL_PAGINATION_RIGHT_EDGE_THRESHOLD_PX = 16;
/** Pull back from the right edge after append so the next page does not immediately re-trigger. */
export const TABLE_PAGINATION_SCROLL_RETREAT_PX = 180;
/** Chart x-axis column pitch — must match MIN_X_AXIS_COL_GAP_PX in ReusableBubbleChart. */
export const CHART_X_AXIS_COLUMN_WIDTH_PX = 120;
/** Vendor data column width — must match packArchitectureTable.columns + styles. */
export const TABLE_VENDOR_COLUMN_WIDTH_PX = 144;
/** Suppress table pagination while chart-driven scroll sync is in flight. */
export const CHART_TO_TABLE_SCROLL_SYNC_GUARD_MS = 200;
export const CHART_TO_TABLE_POST_PAGINATION_SYNC_RETRY_MS = [0, 50, 150, 300];
export const TABLE_SCROLL_Y = 480;
export const TABLE_EMPTY_STATE_MIN_HEIGHT = 252;
export const EMPTY_CELL_VALUE = '--';

/** Figma cell heights by number of visible KPI lines (ticks) in a table cell. */
export const TABLE_CELL_HEIGHT_BY_KPI_COUNT: Record<number, number> = {
    1: 44,
    2: 92,
    3: 138,
};

export const getTableCellHeightForKpiCount = (kpiCount: number): number =>
    TABLE_CELL_HEIGHT_BY_KPI_COUNT[Math.min(Math.max(kpiCount, 1), MAX_KPI_SELECTION)] ?? 44;

const CHART_ONLY_SEGMENT_KPI_IDS = new Set(['color_code', 'radius']);

const compactFormatter = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
});

export const formatTableKpiCheckboxLabel = (label: string) => label.replace(/\s+per\s+/gi, ' / ');

const isPriceKpi = (kpiId: string, yAxisKpiId: string) =>
    kpiId === yAxisKpiId || /price/i.test(kpiId);

/** K/M/B scale suffixes returned separately by the API — not unit labels like oz or %. */
const isCompactScaleSuffix = (suffix: string) => /^[kmb]+$/i.test(suffix.trim());

const withKpiAffixes = (formattedValue: string, prefix?: string, suffix?: string): string => {
    if (!formattedValue || formattedValue === EMPTY_CELL_VALUE) return formattedValue;

    let next = formattedValue;
    const normalizedPrefix = prefix?.trim() ?? '';
    const normalizedSuffix = suffix?.trim() ?? '';

    if (normalizedPrefix && !next.startsWith(normalizedPrefix)) {
        next = `${normalizedPrefix}${next}`;
    }
    if (normalizedSuffix && !next.endsWith(normalizedSuffix)) {
        next = `${next}${normalizedSuffix}`;
    }

    return next;
};

/** Shared KPI display formatter — prefers API-scaled value + affixes (matches backend MetricFormatter). */
export const formatPackArchitectureKpiValue = (kpi: KpiItem, kpiId: string, yAxisKpiId: string): string => {
    const prefix = kpi.valuePrefix ?? '';
    const suffix = kpi.valueSuffix ?? '';
    const apiValue = (kpi.value ?? '').trim();

    if (apiValue && apiValue !== 'null') {
        return withKpiAffixes(apiValue, prefix, suffix);
    }

    if (typeof kpi.raw_value === 'number' && Number.isFinite(kpi.raw_value)) {
        if (isPriceKpi(kpiId, yAxisKpiId)) {
            const formatted = kpi.raw_value.toLocaleString('en-US', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
            });
            return withKpiAffixes(formatted, prefix, suffix);
        }

        const compactSuffix = isCompactScaleSuffix(suffix) ? '' : suffix;
        return withKpiAffixes(compactFormatter.format(kpi.raw_value), prefix, compactSuffix);
    }

    return EMPTY_CELL_VALUE;
};

const buildCellKpiEntry = (kpi: KpiItem, kpiId: string, yAxisKpiId: string): CellKpiEntry => ({
    formatted: formatPackArchitectureKpiValue(kpi, kpiId, yAxisKpiId),
    growthPct: kpi.growth_percentage ?? null,
});

export const getPackArchitectureEntityKey = (entity: { ex_id?: string; description?: string }) =>
    entity.ex_id || entity.description || '';

const getSegmentRowKey = getPackArchitectureEntityKey;

const registerBubbleRepRow = (
    bubbleRepMap: Map<string, { key: string; label: string }>,
    segmentItem: { ex_id?: string; description?: string },
    rowKey: string,
) => {
    if (bubbleRepMap.has(rowKey)) return;

    bubbleRepMap.set(rowKey, {
        key: rowKey,
        label: segmentItem.description || segmentItem.ex_id || rowKey,
    });
};

const mergeSegmentKpis = (
    cell: CellData,
    kpis: KpiItem[] | undefined,
    yAxisKpiId: string,
): CellData => {
    const nextCell = { ...cell };

    for (const kpi of kpis ?? []) {
        if (CHART_ONLY_SEGMENT_KPI_IDS.has(kpi.kpi_id)) continue;
        if (kpi.kpi_id in nextCell) continue;
        nextCell[kpi.kpi_id] = buildCellKpiEntry(kpi, kpi.kpi_id, yAxisKpiId);
    }

    return nextCell;
};

const buildColumnGrid = (
    group: PricePackBubbleRow,
    yAxisKpiId: string,
    bubbleRepMap: Map<string, { key: string; label: string }>,
) => {
    const colKey = group.ex_id || group.description;
    const colCells = new Map<string, CellData>();

    for (const axisGroup of group.data ?? []) {
        for (const segmentItem of axisGroup.data ?? []) {
            const rowKey = getSegmentRowKey(segmentItem);
            if (!rowKey) continue;

            registerBubbleRepRow(bubbleRepMap, segmentItem, rowKey);
            colCells.set(rowKey, mergeSegmentKpis(colCells.get(rowKey) ?? {}, segmentItem.kpis, yAxisKpiId));
        }
    }

    return {
        column: {
            key: colKey,
            label: group.description || group.ex_id || colKey,
        } satisfies TableColumn,
        colCells,
    };
};

export const buildTableData = (rows: PricePackBubbleRow[], yAxisKpiId: string) => {
    const columns: TableColumn[] = [];
    const bubbleRepMap = new Map<string, { key: string; label: string }>();
    const cellGrid = new Map<string, Map<string, CellData>>();

    for (const group of rows) {
        const { column, colCells } = buildColumnGrid(group, yAxisKpiId, bubbleRepMap);
        columns.push(column);
        cellGrid.set(column.key, colCells);
    }

    const tableRows: TableRow[] = Array.from(bubbleRepMap.values()).map(({ key, label }) => {
        const cells: Record<string, CellData> = {};
        for (const [colKey, colCells] of cellGrid) {
            cells[colKey] = colCells.get(key) ?? {};
        }
        return { key, label, cells };
    });

    return { columns, tableRows };
};

const normalizeTableKpiId = normalizePackArchitectureKpiId;

export const findCellKpi = (
    cell: CellData,
    kpiId: string,
    matchesKpiId: (cellKpiId: string, requestedKpiId: string) => boolean = (cellKpiId, requestedKpiId) =>
        normalizeTableKpiId(cellKpiId) === normalizeTableKpiId(requestedKpiId),
): CellKpiEntry | undefined => {
    if (cell[kpiId]) return cell[kpiId];
    const match = Object.entries(cell).find(([key]) => matchesKpiId(key, kpiId));
    return match?.[1];
};

export const hasCellDataForKpis = (
    cell: CellData | undefined,
    kpiIds: string[],
    matchesKpiId?: (cellKpiId: string, requestedKpiId: string) => boolean,
) =>
    kpiIds.some((kpiId) => {
        const entry = findCellKpi(cell ?? {}, kpiId, matchesKpiId);
        return entry && entry.formatted !== EMPTY_CELL_VALUE;
    });

export const isTablePriceKpi = isPriceKpi;

export const getPackArchitectureTableFooterLabel = (
    showTableLoader: boolean,
    isAppendingPages: boolean,
    loadedColumnCount: number,
    effectiveRowCount: number,
) => {
    if (showTableLoader) return 'Loading Results';
    if (isAppendingPages) return 'Loading more…';
    return `Showing ${loadedColumnCount} of ${effectiveRowCount || loadedColumnCount} Records`;
};

export const getPackArchitectureTableEmptyDescription = (
    chartError: string | null,
    isFiltered: boolean,
) => {
    if (chartError) return chartError;
    if (isFiltered) return 'Try adjusting your search';
    return 'No data found for the selected filters';
};

const clampHorizontalScrollLeft = (element: HTMLElement, scrollLeft: number) => {
    const maxScrollLeft = Math.max(0, element.scrollWidth - element.clientWidth);
    return Math.min(Math.max(0, scrollLeft), maxScrollLeft);
};

/** Syncs Ant Design table header + body horizontal scroll (body-only updates leave a header gap). */
export const setPackArchitectureTableScrollLeft = (
    tableWrapper: HTMLElement | null,
    scrollLeft: number,
) => {
    if (!tableWrapper) return;

    const tableBody = tableWrapper.querySelector('.ant-table-body') as HTMLElement | null;
    const tableHeader = tableWrapper.querySelector('.ant-table-header') as HTMLElement | null;
    const headerContent = tableHeader?.querySelector('.ant-table-content') as HTMLElement | null;
    const bodyScrollLeft = tableBody ? clampHorizontalScrollLeft(tableBody, scrollLeft) : scrollLeft;

    if (tableBody) {
        tableBody.scrollTo({ left: bodyScrollLeft, behavior: 'auto' });
    }

    if (headerContent) {
        headerContent.scrollTo({ left: bodyScrollLeft, behavior: 'auto' });
    }

    if (tableHeader) {
        tableHeader.scrollTo({ left: bodyScrollLeft, behavior: 'auto' });
    }
};

export const getMaxHorizontalScrollLeft = (element: HTMLElement) =>
    Math.max(0, element.scrollWidth - element.clientWidth);

export const getHorizontalScrollRatio = (element: HTMLElement) => {
    const maxScrollLeft = getMaxHorizontalScrollLeft(element);
    if (maxScrollLeft <= 0) return 0;
    return element.scrollLeft / maxScrollLeft;
};

/** Price-ladder style: map chart scroll pixels to table by column width ratio (144/120). */
export const syncTableWrapperScrollFromChart = (
    chartElement: HTMLElement,
    tableWrapper: HTMLElement,
) => {
    const scaleFactor = TABLE_VENDOR_COLUMN_WIDTH_PX / CHART_X_AXIS_COLUMN_WIDTH_PX;
    setPackArchitectureTableScrollLeft(tableWrapper, chartElement.scrollLeft * scaleFactor);
};
